/**
 * PDFOverlayEditor.js
 *
 * Visual "drag-and-mark" editor for PDF overlay templates.
 * Renders PDF pages as backend-generated images (fixed DPI)
 * and allows users to place variable field markers at pixel
 * coordinates that map 1:1 to the generated output.
 *
 * Layout:
 *  ┌───────────────────────────────────┬──────────────┐
 *  │  Page Image + Overlay Layer       │  Variable    │
 *  │  (rendered pages with field       │  Sidebar     │
 *  │   markers you can drag/place)     │  (draggable  │
 *  │                                   │   chips +    │
 *  │   ◄ Page 1 of N ►                │   Add new)   │
 *  └───────────────────────────────────┴──────────────┘
 *
 * Coordinates:
 *  - Both editor and backend use pixel coordinates at 200 DPI
 *  - No coordinate conversion needed — what you see is what you get
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiCall } from '../../utils/apiUtils';
import FieldBox from './FieldBox';
import { toast } from 'react-toastify';

const API_BASE = '/api/external/v1/doqsolv';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// ─── PDFOverlayEditor Component ──────────────────────────────────────────────

const PDFOverlayEditor = ({
  sourcePdfId,          // Asset UUID of the source PDF
  fields,               // Array of { key, label, x, y, page, font_size, font_name, color }
  onFieldsChange,       // (newFields) => void — propagate changes to parent
  variables,            // Custom variables list
  standardVars,         // Standard variables list
  onAddVariable,        // (newVar) => void — add a new custom variable
  onRemoveVariable,     // (key) => void — remove a custom variable
  onUpdateVariable,     // (key, updates) => void — update a custom variable's properties
}) => {
  // Page images from backend
  const [pageData, setPageData] = useState([]); // [{ page, width, height, image_url }]
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Interaction
  const [selectedFieldIdx, setSelectedFieldIdx] = useState(null);

  // Add variable form
  const [addVarOpen, setAddVarOpen] = useState(false);
  const [newVarLabel, setNewVarLabel] = useState('');
  const [newVarKey, setNewVarKey] = useState('');
  const [newVarType, setNewVarType] = useState('text');

  // Variable settings panel
  const [settingsVar, setSettingsVar] = useState(null);

  // Image display scaling (fit to container)
  const [displayScale, setDisplayScale] = useState(1);
  const containerRef = useRef(null);

  // ── Load PDF page images from backend ────────────────────────────────

  useEffect(() => {
    if (!sourcePdfId) {
      setError('No source PDF configured');
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadPages = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiCall(`${API_BASE}/assets/${sourcePdfId}/pages`);
        if (!cancelled) {
          setPageData(res.pages || []);
          setCurrentPage(1);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Page image load error:', err);
          setError(`Failed to load PDF pages: ${err.message || 'Unknown error'}`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPages();
    return () => { cancelled = true; };
  }, [sourcePdfId]);

  // ── Compute display scale to fit image in container ──────────────────

  const currentPageData = pageData.find((p) => p.page === currentPage);
  const imageWidth = currentPageData?.width || 0;
  const imageHeight = currentPageData?.height || 0;

  useEffect(() => {
    if (!containerRef.current || !imageWidth) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const containerWidth = entry.contentRect.width - 32; // padding
        if (imageWidth > 0 && containerWidth > 0) {
          setDisplayScale(Math.min(1, containerWidth / imageWidth));
        }
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, [imageWidth]);

  // ── Get fields for current page ────────────────────────────────────

  const currentPageFields = (fields || [])
    .map((f, idx) => ({ ...f, _idx: idx }))
    .filter((f) => (f.page || 1) === currentPage);

  // ── Handle field drag end (reposition) ─────────────────────────────

  const handleFieldDragEnd = useCallback((fieldIdx, newX, newY) => {
    // Convert display coordinates back to image pixel coordinates
    const x = newX / displayScale;
    const y = newY / displayScale;

    const updated = [...(fields || [])];
    updated[fieldIdx] = { ...updated[fieldIdx], x, y };
    onFieldsChange(updated);
  }, [fields, displayScale, onFieldsChange]);

  // ── Handle drop from sidebar (new field) ───────────────────────────

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    try {
      const raw = e.dataTransfer?.getData('text/plain');
      if (!raw) return;
      const varData = JSON.parse(raw);
      if (!varData?.key) return;

      // Calculate drop position relative to the image container
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      // Convert display coordinates to image pixel coordinates
      const x = (e.clientX - rect.left) / displayScale;
      const y = (e.clientY - rect.top) / displayScale;

      // Check if this variable is already placed on this page
      const existing = (fields || []).find(
        (f) => f.key === varData.key && (f.page || 1) === currentPage
      );
      if (existing) {
        // Move existing instead of duplicating
        const idx = fields.indexOf(existing);
        const updated = [...fields];
        updated[idx] = { ...updated[idx], x, y };
        onFieldsChange(updated);
        return;
      }

      // Add new field
      const newField = {
        key: varData.key,
        label: varData.label,
        x,
        y,
        page: currentPage,
        font_size: 12,
        font_name: 'Helvetica',
        color: '#000000',
      };

      onFieldsChange([...(fields || []), newField]);
    } catch {
      // Invalid drop data
    }
  }, [fields, currentPage, displayScale, onFieldsChange]);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  // ── Delete Field ───────────────────────────────────────────────────

  const handleDeleteField = useCallback((fieldIdx) => {
    const updated = [...(fields || [])];
    updated.splice(fieldIdx, 1);
    onFieldsChange(updated);
    setSelectedFieldIdx(null);
  }, [fields, onFieldsChange]);

  // ── Drag start from sidebar ────────────────────────────────────────

  const handleVarDragStart = useCallback((e, variable) => {
    e.dataTransfer.setData('text/plain', JSON.stringify(variable));
    e.dataTransfer.effectAllowed = 'copy';
  }, []);

  // ── Add custom variable ────────────────────────────────────────────

  const handleAddVariable = () => {
    if (!newVarKey.trim() || !newVarLabel.trim()) {
      toast.warning('Variable key and label are required');
      return;
    }

    const sanitisedKey = newVarKey
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, '_')
      .replace(/^[^a-z]/, 'v_');

    // Check duplicates
    const allVars = [...(standardVars || []), ...(variables || [])];
    if (allVars.some((v) => v.key === sanitisedKey)) {
      toast.warning('A variable with this key already exists');
      return;
    }

    const newVar = {
      key: sanitisedKey,
      label: newVarLabel.trim(),
      var_type: newVarType,
      is_standard: false,
      is_required: true,
    };

    if (onAddVariable) onAddVariable(newVar);
    setNewVarKey('');
    setNewVarLabel('');
    setNewVarType('text');
    setAddVarOpen(false);
    toast.success(`Variable "${newVar.label}" added`);
  };

  // ── Click on image to deselect ─────────────────────────────────────

  const handleCanvasClick = useCallback(() => {
    setSelectedFieldIdx(null);
  }, []);

  // ── Render ─────────────────────────────────────────────────────────

  const allVars = [...(standardVars || []), ...(variables || [])];
  const numPages = pageData.length;

  return (
    <div className="flex gap-3 flex-1 min-h-0 overflow-hidden">
      {/* PDF Page Image Area */}
      <div className="flex-1 flex flex-col bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden">
        {/* Page Navigation */}
        <div className="border-b border-gray-200 px-4 py-2 flex items-center justify-between bg-gray-50 flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              PDF Overlay Editor
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 transition"
              title="Previous page"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-sm text-gray-600 font-medium min-w-[80px] text-center">
              Page {currentPage} / {numPages || '…'}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(numPages, p + 1))}
              disabled={currentPage >= numPages}
              className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 transition"
              title="Next page"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          <div className="text-[10px] text-gray-400">
            Drop variables onto the page
          </div>
        </div>

        {/* Image + Overlay Container */}
        <div className="flex-1 overflow-auto bg-gray-200 flex items-start justify-center p-4" ref={containerRef}>
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mb-3" />
              <p className="text-sm">Loading PDF pages…</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 text-red-400">
              <svg className="w-8 h-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm">{error}</p>
            </div>
          ) : currentPageData ? (
            <div
              className="relative shadow-lg bg-white"
              style={{
                width: imageWidth * displayScale,
                height: imageHeight * displayScale,
              }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={handleCanvasClick}
            >
              {/* Page Image */}
              <img
                src={`${BACKEND_URL}${currentPageData.image_url}`}
                alt={`Page ${currentPage}`}
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'block',
                  pointerEvents: 'none',
                }}
                draggable={false}
              />

              {/* Field Overlay Layer */}
              {currentPageFields.map((field) => (
                <FieldBox
                  key={`${field.key}-${field.page}`}
                  field={{
                    ...field,
                    // Scale pixel coords to display coords for rendering
                    uiX: (field.x || 0) * displayScale,
                    uiY: (field.y || 0) * displayScale,
                  }}
                  selected={selectedFieldIdx === field._idx}
                  onSelect={() => setSelectedFieldIdx(field._idx)}
                  onDragEnd={(newX, newY) => handleFieldDragEnd(field._idx, newX, newY)}
                  onDelete={() => handleDeleteField(field._idx)}
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 py-20">No page data available</p>
          )}
        </div>
      </div>

      {/* Variable Sidebar */}
      <div className="w-64 flex-shrink-0 bg-white rounded-lg border border-gray-100 shadow-sm flex flex-col overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <h3 className="text-sm font-semibold text-gray-700">Variables</h3>
          <p className="text-xs text-gray-400 mt-0.5">Drag onto the page to place</p>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
          {/* Standard Variables */}
          <div>
            <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1.5">Standard</p>
            <div className="space-y-1">
              {(standardVars || []).map((v) => {
                const placed = (fields || []).some(
                  (f) => f.key === v.key && (f.page || 1) === currentPage
                );
                return (
                  <div
                    key={v.key}
                    draggable
                    onDragStart={(e) => handleVarDragStart(e, v)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium cursor-grab active:cursor-grabbing transition select-none border ${
                      placed
                        ? 'bg-green-50 text-green-700 border-green-200'
                        : 'bg-indigo-50 text-indigo-700 border-transparent hover:border-indigo-200'
                    }`}
                    title={placed ? `✓ Placed on page ${currentPage}` : `Drag to place {{${v.key}}}`}
                  >
                    <svg className="w-3.5 h-3.5 flex-shrink-0 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                    </svg>
                    <span className="truncate flex-1">{v.label}</span>
                    {placed && (
                      <svg className="w-3.5 h-3.5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Custom Variables */}
          <div>
            <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1.5">Custom</p>
            {(variables || []).length === 0 && !addVarOpen && (
              <p className="text-xs text-gray-400 italic mb-2">No custom variables yet.</p>
            )}
            <div className="space-y-1">
              {(variables || []).map((v) => {
                const placed = (fields || []).some(
                  (f) => f.key === v.key && (f.page || 1) === currentPage
                );
                return (
                  <div
                    key={v.key}
                    draggable
                    onDragStart={(e) => handleVarDragStart(e, v)}
                    className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-xs font-medium cursor-grab active:cursor-grabbing transition select-none border ${
                      placed
                        ? 'bg-green-50 text-green-700 border-green-200'
                        : 'bg-emerald-50 text-emerald-700 border-transparent hover:border-emerald-200'
                    }`}
                    title={placed ? `✓ Placed on page ${currentPage}` : `Drag to place {{${v.key}}}`}
                  >
                    <div className="flex items-center gap-2 truncate">
                      <svg className="w-3.5 h-3.5 flex-shrink-0 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                      </svg>
                      <span className="truncate">{v.label}</span>
                      {placed && (
                        <svg className="w-3.5 h-3.5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={(e) => { e.stopPropagation(); setSettingsVar(v); }}
                        className="text-emerald-400 hover:text-emerald-600 flex-shrink-0"
                        title="Variable settings"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); onRemoveVariable && onRemoveVariable(v.key); }}
                        className="text-red-400 hover:text-red-600 flex-shrink-0"
                        title="Remove variable"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Variable Settings Panel */}
            {settingsVar && (
              <div className="mt-2 p-3 bg-indigo-50 rounded-lg border border-indigo-200 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-indigo-700">⚙ {settingsVar.label}</p>
                  <button
                    onClick={() => setSettingsVar(null)}
                    className="text-gray-400 hover:text-gray-600 text-xs"
                  >
                    ✕
                  </button>
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 block">Label</label>
                  <input
                    value={settingsVar.label}
                    onChange={(e) => {
                      const newLabel = e.target.value;
                      setSettingsVar((prev) => ({ ...prev, label: newLabel }));
                      onUpdateVariable && onUpdateVariable(settingsVar.key, { label: newLabel });
                    }}
                    className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-indigo-500 outline-none"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 block">Type</label>
                  <select
                    value={settingsVar.var_type || 'text'}
                    onChange={(e) => {
                      const newType = e.target.value;
                      setSettingsVar((prev) => ({ ...prev, var_type: newType }));
                      onUpdateVariable && onUpdateVariable(settingsVar.key, { var_type: newType });
                    }}
                    className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-indigo-500 outline-none"
                  >
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                    <option value="date">Date</option>
                    <option value="textarea">Textarea</option>
                    <option value="currency">Currency</option>
                    <option value="mobile">Mobile</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 block">Default Value</label>
                  <input
                    value={settingsVar.default_value || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSettingsVar((prev) => ({ ...prev, default_value: val }));
                      onUpdateVariable && onUpdateVariable(settingsVar.key, { default_value: val });
                    }}
                    className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-indigo-500 outline-none"
                  />
                </div>
                <div className="flex gap-3">
                  <label className="flex items-center gap-1.5 text-[10px] text-gray-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!settingsVar.uppercase}
                      onChange={(e) => {
                        setSettingsVar((prev) => ({ ...prev, uppercase: e.target.checked }));
                        onUpdateVariable && onUpdateVariable(settingsVar.key, { uppercase: e.target.checked });
                      }}
                      className="rounded border-gray-300 text-indigo-600"
                    />
                    Uppercase
                  </label>
                  <label className="flex items-center gap-1.5 text-[10px] text-gray-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settingsVar.is_required !== false}
                      onChange={(e) => {
                        setSettingsVar((prev) => ({ ...prev, is_required: e.target.checked }));
                        onUpdateVariable && onUpdateVariable(settingsVar.key, { is_required: e.target.checked });
                      }}
                      className="rounded border-gray-300 text-indigo-600"
                    />
                    Required
                  </label>
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 block">Group</label>
                  <input
                    value={settingsVar.group || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSettingsVar((prev) => ({ ...prev, group: val }));
                      onUpdateVariable && onUpdateVariable(settingsVar.key, { group: val });
                    }}
                    placeholder="e.g. Party Details"
                    className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-indigo-500 outline-none"
                  />
                </div>
              </div>
            )}

            {/* Add Variable Form */}
            {addVarOpen ? (
              <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
                <input
                  value={newVarLabel}
                  onChange={(e) => {
                    setNewVarLabel(e.target.value);
                    setNewVarKey(
                      e.target.value
                        .trim()
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, '_')
                        .replace(/^[^a-z]/, 'v_')
                    );
                  }}
                  placeholder="Label (e.g. Monthly Rent)"
                  className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-indigo-500 outline-none"
                />
                <div className="flex gap-2">
                  <input
                    value={newVarKey}
                    onChange={(e) => setNewVarKey(e.target.value)}
                    placeholder="Key"
                    className="flex-1 px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-indigo-500 outline-none font-mono"
                  />
                  <select
                    value={newVarType}
                    onChange={(e) => setNewVarType(e.target.value)}
                    className="px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-indigo-500 outline-none"
                  >
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                    <option value="date">Date</option>
                    <option value="currency">Currency</option>
                    <option value="mobile">Mobile</option>
                  </select>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleAddVariable}
                    className="flex-1 text-xs text-white px-3 py-1.5 rounded font-medium"
                    style={{ background: '#10b981' }}
                  >
                    Add
                  </button>
                  <button
                    onClick={() => { setAddVarOpen(false); setNewVarKey(''); setNewVarLabel(''); }}
                    className="flex-1 text-xs text-gray-600 px-3 py-1.5 rounded border border-gray-200 hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setAddVarOpen(true)}
                className="w-full mt-2 text-xs text-emerald-600 px-3 py-2 rounded-lg border border-dashed border-emerald-300 hover:bg-emerald-50 transition font-medium"
              >
                + Variable
              </button>
            )}
          </div>
        </div>

        {/* Field Properties Panel (when a field is selected) */}
        {selectedFieldIdx !== null && fields[selectedFieldIdx] && (
          <div className="border-t border-gray-200 px-3 py-3 bg-gray-50 flex-shrink-0 space-y-2">
            <p className="text-xs font-semibold text-gray-600">Field Properties</p>
            <div>
              <label className="text-[10px] text-gray-400 block">Font Size (pt)</label>
              <input
                type="number"
                min={6}
                max={72}
                value={fields[selectedFieldIdx].font_size || 12}
                onChange={(e) => {
                  const updated = [...fields];
                  updated[selectedFieldIdx] = {
                    ...updated[selectedFieldIdx],
                    font_size: parseInt(e.target.value, 10) || 12,
                  };
                  onFieldsChange(updated);
                }}
                className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-indigo-500 outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] text-gray-400 block">Color</label>
              <input
                type="color"
                value={fields[selectedFieldIdx].color || '#000000'}
                onChange={(e) => {
                  const updated = [...fields];
                  updated[selectedFieldIdx] = {
                    ...updated[selectedFieldIdx],
                    color: e.target.value,
                  };
                  onFieldsChange(updated);
                }}
                className="w-full h-7 border border-gray-200 rounded cursor-pointer"
              />
            </div>
            <div>
              <label className="text-[10px] text-gray-400 block mb-1">Style</label>
              <div className="flex gap-1">
                <button
                  onClick={() => {
                    const updated = [...fields];
                    updated[selectedFieldIdx] = {
                      ...updated[selectedFieldIdx],
                      bold: !updated[selectedFieldIdx].bold,
                    };
                    onFieldsChange(updated);
                  }}
                  className={`flex-1 px-2 py-1 text-xs font-bold rounded border transition ${
                    fields[selectedFieldIdx].bold
                      ? 'bg-indigo-100 text-indigo-700 border-indigo-400'
                      : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                  }`}
                  title="Toggle bold"
                >
                  B
                </button>
                <button
                  onClick={() => {
                    const updated = [...fields];
                    updated[selectedFieldIdx] = {
                      ...updated[selectedFieldIdx],
                      underline: !updated[selectedFieldIdx].underline,
                    };
                    onFieldsChange(updated);
                  }}
                  className={`flex-1 px-2 py-1 text-xs underline rounded border transition ${
                    fields[selectedFieldIdx].underline
                      ? 'bg-indigo-100 text-indigo-700 border-indigo-400'
                      : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                  }`}
                  title="Toggle underline"
                >
                  U
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 flex-shrink-0">
          <p className="text-[10px] text-gray-400 leading-tight">
            📌 Drag variables and drop them on the page. Click a placed field to edit its properties.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PDFOverlayEditor;
